import React, { useContext, createContext } from 'react';
import {
  PlatformFunctionalityProviderProps,
  PlatformFunctionalityContextInterface,
  PlatformFunctionality,
} from '../types';

export const PlatformFunctionalityContext = createContext<PlatformFunctionalityContextInterface>({});

/**
 * Provides a way to grant shared components access to functionality specific to consuming
 * platforms (e.g. Hydrogen or React Router's `Link` components)
 *
 * @param children child components which will have access to the provider's functionality
 * @param linkComponent for internal link navigation in the hydrogen app, this link component should be provided as the Link component from hydrogen
 * else, the default a tag will be used
 * @param imageComponent for internal images in the hydrogen app, this image component should be provided as the Image component
 * else, the default img tag will be used
 * @param navigateFunction can be called to programmatically navigate to `path`. Falls back to setting `location.href`.
 */
export const PlatformFunctionalityProvider = ({
  children,
  linkComponent,
  imageComponent,
  navigateFunction,
}: PlatformFunctionalityProviderProps): React.ReactElement => {
  return (
    <PlatformFunctionalityContext.Provider value={{ linkComponent, imageComponent, navigateFunction }}>
      {children}
    </PlatformFunctionalityContext.Provider>
  );
};

export const usePlatformFunctionality = (): PlatformFunctionality => {
  const context = useContext(PlatformFunctionalityContext);

  if (!context) {
    return {};
  }
  return {
    LinkComponent: context.linkComponent,
    ImageComponent: context.imageComponent,
    navigateFunction: context.navigateFunction,
  };
};
